import React, { useContext, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AnnotationsModule from "highcharts/modules/annotations";
import { Box, CircularProgress, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import APIContext from "../../../services/api/api";
import { URLS } from "../../../configuration/urls";
import { onShowToast } from "../../../components/toast";
import { useParcelsMawb } from "../../../services/clear-customs/mawb";

AnnotationsModule(Highcharts);


export function ParcelsHandledAndDScan({ id }) {
  const { getData } = useContext(APIContext);
  const { formatMessage } = useIntl();
  const [data, setData] = useState([]);
  const { showToast } = onShowToast();
  const [isLoading, setIsLoading] = useState(false);
  const { mawbInfoCard } = useParcelsMawb();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const result = await getData(`h7/kpi/mawb/${id}/parcels`, {}, URLS.EC_CLEAR_CUSTOMS_BE());
        if (Array.isArray(result)) setData(result || []);
      } catch (err) {
        showToast({
          message: err,
          type: "error"
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [id]);

  const categories = data?.map(item => item?.date || "");
  const allParcels = data?.map(item => item?.parcelsCount || 0);
  const handledParcels = data?.map(item => item?.handledParcelsCount || 0);
  const dScannedParcels = data?.map(item => item?.doneScannedParcelsCount || 0);

  const hasValidDeviationData =
    Array.isArray(data) &&
    data.some(
      item =>
        item?.date != null &&
        item?.doneScannedParcelsCount != null &&
        item?.handledParcelsCount != null &&
        item?.parcelsCount != null
    );
  const handledLabels = data?.map((item, index) => ({
    point: { xAxis: 0, yAxis: 0, x: index, y: handledParcels[index] },
    text: `${handledParcels[index].toLocaleString("de-DE")}`,
    borderColor: "var(--innovation-100)",
    style: {
      color: "var(--text-secondary)",
      fontWeight: "bold",
      fontSize: "12px"
    },
    y: -10,
    x: 100,
    shape: "connector",
    connectorColor: "var(--innovation)",
    connectorWidth: 1.5
  })) || [];

  const dScannedLabels = data?.map((item, index) => ({
    point: { xAxis: 0, yAxis: 0, x: index, y: dScannedParcels[index] },
    text: `${dScannedParcels[index].toLocaleString("de-DE")}`,
    borderColor: "var(--success)",
    style: {
      color: "var(--text-secondary)",
      fontWeight: "bold",
      fontSize: "12px"
    },
    y: -10,
    x: -100,
    shape: "connector",
    connectorColor: "var(--success)",
    connectorWidth: 1.5
  })) || [];

  const annotations = [
    {
      id: "handled-annotations-group",
      visible: true,
      labels: handledLabels
    },
    {
      id: "dscanned-annotations-group",
      visible: true,
      labels: dScannedLabels
    }
  ];

  const options = {
    chart: {
      type: "column",
      height: hasValidDeviationData ? 400 : 100
    },
    title: { text: null },
    xAxis: {
      categories,
      borderWidth: 100,
      lineWidth: hasValidDeviationData ? 4 : 0,
      lineColor: "var(--text-secondary)",
      labels: { enabled: false }
    },
    yAxis: {
      min: 0,
      visible: false,
      title: { text: null }
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      itemStyle: { fontWeight: "bold", fontSize: "16px", color: "var(--text-secondary)" }
    },
    tooltip: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      column: {
        grouping: false,
        borderWidth: 0
      },
      series: {
        dataLabels: { enabled: false },
        events: {
          legendItemClick: function() {
            const series = this;
            const chart = series.chart;
            const linkedAnnotationId = series.options.linkedAnnotationId;

            if (linkedAnnotationId) {
              const annotation = chart.annotations.find(
                (a) => a.options.id === linkedAnnotationId
              );

              if (annotation) {
                const newVisibility = !series.visible;
                annotation.update({ visible: newVisibility });
              }
            }
          }
        }
      }
    },
    series: [
      {
        name: `<span style=\"font-size:20px;color:var(--input-label);\">${formatMessage({ id: "allParcels" })} (${(mawbInfoCard?.numberOfParcels || 0)?.toLocaleString?.("de-DE")} Parcels)</span>`,
        data: allParcels,
        color: "var(--innovation-light)",
        pointPadding: 0.1,
        borderWidth: 0
      },
      {
        name: `<span style=\"font-size:20px;color:var(--input-label);\">${formatMessage({ id: "handledParcels" })}</span>`,
        data: handledParcels,
        color: "var(--innovation-100)",
        pointPadding: 0.1,
        borderWidth: 0,
        linkedAnnotationId: "handled-annotations-group"
      },
      {
        name: `<span style=\"font-size:20px;color:var(--input-label);\">${formatMessage({ id: "dScannedParcels" })}</span>`,
        data: dScannedParcels,
        color: "var(--success)",
        pointPadding: 0.1,
        borderWidth: 0,
        linkedAnnotationId: "dscanned-annotations-group",
        dataLabels: {
          enabled: true,
          verticalAlign: "top",
          align: "center",
          inside: true,
          style: {
            color: "white",
            fontWeight: "bold",
            textOutline: "none",
            fontSize: "12px"
          },
          formatter: function() {
            const total = allParcels[this.point.index] || 1;
            const percent = Math.round((this.y / total) * 100);
            return `${percent}%`;
          }
        }
      }
    ],
    annotations
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ borderRadius: 2, overflow: "hidden" }} backgroundColor="var(--container-light)">
        {/* ... Header logic ... */}
        <Box backgroundColor="var(--container-light)" sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2px solid",
          py: 1,
          px: 2
        }}>
          <Typography color={"var(--text-primary)"} variant="subtitle1" fontWeight={600} fontSize={"20px"}
            data-testid={`parcels_handled_dscan_box_heading`}>
            <FormattedMessage id="accumulativeParcelsHandledAndDScanned"
              defaultMessage="Accumulative Parcels Handled and D-scanned" />
          </Typography>
        </Box>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </Box>
    </>
  );
}
